import { CircularProgress } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Document, Page } from "react-pdf";

interface Props {
  url: string;
  isLoading: boolean;
  client: string;
  presentation?: "modal" | "continuous";
}

const ScrollPdfPreview: React.FC<Props> = ({ url, isLoading, client, presentation = "modal" }) => {
  const { t, i18n } = useTranslation();
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageWidth, setPageWidth] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const isContinuous = presentation === "continuous";

  useEffect(() => {
    if (!isContinuous || !containerRef.current) {
      return;
    }

    const observer = new ResizeObserver(([entry]) => {
      setPageWidth(entry.contentRect.width);
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [isContinuous]);

  function onDocumentLoadSuccess({ numPages }: { numPages: number }) {
    setNumPages(numPages);
  }

  return (
    <div
      ref={containerRef}
      className={isContinuous ? "pdf-preview-continuous" : "pdf-preview-container w-full lg:min-w-840"}
    >
      {isLoading && (
        <div className="text-center">
          <p>
            {i18n.exists(`common.generatingPDF.${client}`)
              ? t(`common.generatingPDF.${client}`)
              : t(`common.generatingPDF.default`)}
          </p>
          <div className="pt-40">
            <CircularProgress size={80} />
          </div>
        </div>
      )}
      {!isLoading && (
        <Document file={url} onLoadSuccess={onDocumentLoadSuccess} renderMode="canvas">
          {Array.from({ length: !isContinuous || pageWidth > 0 ? (numPages ?? 0) : 0 }, (_el, index) => (
            <div className={isContinuous ? "mb-4 last:mb-0" : "lg:mb-10"} key={`page_${index + 1}`}>
              <Page
                className={isContinuous ? "shadow-lg" : "shadow-lg m-3"}
                width={isContinuous ? pageWidth : 780}
                pageNumber={index + 1}
                renderTextLayer={false}
                renderAnnotationLayer={false}
              />
            </div>
          ))}
        </Document>
      )}
    </div>
  );
};

export default ScrollPdfPreview;
